import {tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {GlobalService} from 'src/app/services/global.service';
import {MixpanelService} from 'src/app/services/MixPanel.service';
import {GoogleAnalyticsService} from 'src/app/services/google-analytics.service';

import * as globalPaths from '../../pathConfig';
import {UserIdleTimerService} from '../../services/userIdle.service';
import {getStoredUTMParams, UTM_KEYS, TRACKING_KEYS, REFERRER_KEYS} from '../../utils/tracking.utils';
import {UTMParams, TrackingParams} from '../../utils/tracking.types';
import {getClientDeviceInfo} from '../../utils/device-fingerprint.utils';

@Injectable({providedIn: 'root'})
export class AuthService {
    userData;
    /** constructor */
    constructor(
        private readonly http: HttpClient,
        private readonly global: GlobalService,
        private readonly mixpanelService: MixpanelService,
        private readonly googleAnalytics: GoogleAnalyticsService,
        private userIdle: UserIdleTimerService
    ) {}

    /**
     * login and verify the user details
     *
     * @param payload user login details
     */
    login(payload: object) {
        const deviceInfo = getClientDeviceInfo();
        const loginPayload = {
            ...payload,
            ...deviceInfo,
        };
        // show loading indicator and send an AJAX request to verify login details.
        this.global.showLoading();
        this.global.sendAjaxRequest('login/verifyLoginDetails', loginPayload).subscribe((loginResponse) => {
            if (loginResponse.message === 'successful') {
                this.global.setLocalStorage('tenantpayToken', loginResponse.token);
                this.userIdle.startWatching();
                this.global.initLoggedInUser(true);
            } else if (loginResponse.message === 'superadmin_not_authorized') {
                this.global.basicAlert('', 'label.superadmin_not_authorized', true);
                this.global.hideLoading();
            } else if (loginResponse.message === 'user_not_authorized') {
                this.global.hideLoading();
                this.redirectToTenantPortal();
            } else if (loginResponse.message === 'company_not_found') {
                this.global.basicAlert('', 'Company is Inactive.', true);
                this.global.hideLoading();
            } else if (loginResponse.message === 'user_not_verified') {
                this.userData = {
                    ...payload,
                    isLoginFlow: true,
                };
                this.global.hideLoading();
                this.global.pushPage('verify-otp');
            } else {
                this.global.basicAlert('', 'label.enter_valid_credentials', true);
                this.global.hideLoading();
            }
        });
    }

    /**
     * shows the tenant account message and redirects to the tenant portal login page
     */
    private async redirectToTenantPortal() {
        await this.global.basicAlert(
            '',
            'This email is registered as a tenant account. Redirecting you to the tenant portal to sign in.'
        );
        setTimeout(() => (window.location.href = globalPaths.TENANT_URL), 2500);
    }

    /**
     * sends the forgot password email
     *
     * @param userName username of the user
     */
    forgotPassword(userName: string) {
        // show loading indicator and send an AJAX request to initiate forgot password request
        this.global.showLoading();
        return this.global
            .sendAjaxRequest('login/forgotPasswordEmail', {userName, userType: 'managerPortalUser'})
            .pipe(tap(() => this.global.hideLoading()));
    }

    /**
     * create a new user
     *
     * @param payload new user details
     */

    signUp(payload: object) {
        const requestURL = this.global.API_URL + '/user/createManager';
        const { utm, tracking } = getStoredUTMParams();

        const utmParams: UTMParams = {};
        if (utm.source) utmParams.source = utm.source;
        if (utm.medium) utmParams.medium = utm.medium;
        if (utm.campaign) utmParams.campaign = utm.campaign;
        if (utm.term) utmParams.term = utm.term;
        if (utm.content) utmParams.content = utm.content;

        const trackingParams: TrackingParams = {};
        if (tracking) {
            if (tracking.gclid) trackingParams.gclid = tracking.gclid;
            if (tracking.fbclid) trackingParams.fbclid = tracking.fbclid;
            if (tracking.msclkid) trackingParams.msclkid = tracking.msclkid;
            if (tracking.ttclid) trackingParams.ttclid = tracking.ttclid;
            if (tracking.liFatId) trackingParams.liFatId = tracking.liFatId;
            if (tracking.twclid) trackingParams.twclid = tracking.twclid;
            if (tracking.gbraid) trackingParams.gbraid = tracking.gbraid;
            if (tracking.wbraid) trackingParams.wbraid = tracking.wbraid;
            if (tracking.utmId) trackingParams.utmId = tracking.utmId;
            if (tracking.referrerUrl) trackingParams.referrerUrl = tracking.referrerUrl;
            if (tracking.referrerDomain) trackingParams.referrerDomain = tracking.referrerDomain;
        }

        const enrichedPayload: Record<string, any> = {
            ...payload,
        };

        if (Object.keys(utmParams).length > 0) {
            enrichedPayload.utm = utmParams;
        }

        if (Object.keys(trackingParams).length > 0) {
            enrichedPayload.tracking = trackingParams;
        }

        return this.http.post(requestURL, enrichedPayload).subscribe((response: any) => {
            if (response.message === 'created') {
                // Clear all tracking/UTM data from localStorage and cookies after successful signup
                const allTrackingKeys = [...UTM_KEYS, ...TRACKING_KEYS, ...REFERRER_KEYS];
                allTrackingKeys.forEach((key) => {
                    localStorage.removeItem(key);
                    // Remove corresponding cookie by setting max-age to 0
                    document.cookie = `${key}=; path=/; max-age=0`;
                });
                if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                    window.gtag('event', 'sign_up', { method: 'email' });
                }
                this.mixpanelService.track('sign_up', { method: 'email' });
                this.userData = response.data;
                this.googleAnalytics.trackSignUp('email');
                this.global.pushPage('verify-otp');
            }
        });
    }

    /**
     * sends verification code to user's email
     *
     * @param email user's email address
     */
    sendVerificationCode(email: string) {
        return this.global.sendAjaxRequest('login/sendVerificationCode', {email});
    }

    verifyOtp(email, otp) {
        const requestURL = this.global.API_URL + '/user/verifySignUp';
        const dataToSend = {
            request: {
                email: email,
                verificationCode: otp,
            },
        };
        return this.http.post(requestURL, dataToSend).subscribe((response: any) => {
            if (response.message === 'verification_successful') {
                this.global.setLocalStorage('tenantpayToken', response.token);
                this.global.pushPage('kyc-portal');
            } else if (
                response.message === 'invalid_verification_code' ||
                response.message === 'verification_code_expired'
            ) {
                this.global.basicAlert('', 'Please check your email for a new code.');
            }
        });
    }
}
